import { useEffect, useState } from 'react';

/**
 * Session-scoped persistence for view state (filters, selections).
 *
 * ## Why sessionStorage and not localStorage
 *
 * The PRDs specify **defaults** for every control (`PRD.md` §6.4, `PRD-Delivery.md` §6.6) and
 * SC-010 asserts them "in 100% of sessions". `sessionStorage` is per-tab and dies with the tab, so
 * a fresh session still opens on the defaults while navigating away and back — or reloading — no
 * longer throws the user's selections away. `localStorage` would outlive the session and put a
 * months-old filter combination in front of someone who believes they are looking at the default
 * view, which is how a person misreads a number rather than a control.
 *
 * ## Why every read is sanitised
 *
 * A stored value is **untrusted input**: it was written by an older deploy, or by hand. A filter
 * value that no endpoint accepts would turn a restored session into a wall of 422s, so
 * `sanitize` returns `null` for anything it does not recognise and the caller falls back to its
 * defaults. Persisted state must never be able to leave the page unable to load.
 *
 * Storage itself is treated as optional throughout — Safari's private mode throws on access
 * rather than returning null, and a page that cannot remember a filter must still render.
 */

const PREFIX = 'bob:';

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function loadPersisted<T>(key: string, sanitize: (raw: unknown) => T | null): T | null {
  const store = storage();
  if (!store) return null;

  try {
    const raw = store.getItem(PREFIX + key);
    if (raw == null) return null;
    return sanitize(JSON.parse(raw) as unknown);
  } catch {
    // Unparseable or unreadable: indistinguishable from "nothing stored", and handled the same.
    return null;
  }
}

export function savePersisted(key: string, value: unknown): void {
  const store = storage();
  if (!store) return;

  try {
    store.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    // Quota or a locked-down browser. Losing the memory of a filter is not worth an error state.
  }
}

/**
 * `useState`, plus a memory that survives unmount and reload.
 *
 * `storageKey` is optional so a caller can opt out and get plain component state — that is what
 * every surface other than Delivery still does today, and it keeps this change from silently
 * altering pages nobody asked about.
 */
export function usePersistentState<T>(
  storageKey: string | undefined,
  initial: T,
  sanitize: (raw: unknown) => T | null,
): [T, (next: T | ((current: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (!storageKey) return initial;
    return loadPersisted(storageKey, sanitize) ?? initial;
  });

  // Written from an effect rather than from the updater: React may invoke an updater twice, and
  // state changes belong in one place even when the side effect happens to be idempotent.
  useEffect(() => {
    if (!storageKey) return;
    savePersisted(storageKey, value);
  }, [storageKey, value]);

  return [value, setValue];
}

/**
 * A sanitiser for a flat record of enum-valued keys — every filter store in this app.
 *
 * Unknown keys are dropped and unrecognised values fall back to the default for that key alone,
 * so one stale field cannot discard an otherwise valid restore.
 */
export function sanitizeEnumRecord<T extends { [K in keyof T]: string }>(
  defaults: T,
  allowed: { [K in keyof T]: readonly T[K][] },
): (raw: unknown) => T | null {
  return (raw: unknown) => {
    if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const source = raw as Record<string, unknown>;

    const result = { ...defaults };
    const writable = result as unknown as Record<string, string>;

    for (const key of Object.keys(defaults) as Array<keyof T & string>) {
      const candidate = source[key];
      if (typeof candidate === 'string' && (allowed[key] as readonly string[]).includes(candidate)) {
        writable[key] = candidate;
      }
    }
    return result;
  };
}
